"use client"

import { useEffect, useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import Home from "../home/Home"
import { DialogActions } from "@mui/material"
import { useAppContext } from "../../../shared/functions/Context"
import { totalQ2MeasureRating, totalQ4MeasureRating } from "../../shared/functions/Scorecard"
import RatingsOverview from "./Charts/RatingsOverview"
import UserRatingStatus from "../questionnaire/views/UserRatingStatus"
import {
  TrendingUp,
  Users,
  FileText,
  Award,
  ChevronRight,
  Info,
  Sun,
  Moon,
  Sunrise,
  Clock,
  Target,
  Activity,
  Calendar,
  X,
} from "lucide-react"
import NoSessionAlert from "../ratings/components/NoSessionAlert"

export const UserDashboard = () => {
  const { api, store } = useAppContext()

  const me = store.auth.meJson
  let uid = ""
  if (me !== null) {
    uid = me?.uid ?? ""
  }

  const valueRatings = store.valueRating.all.filter(
    (value) =>
      value.asJson.rateeId === me?.uid &&
      value.asJson.adminApproval !== "Approved for Re-rating" &&
      value.asJson.resubmission !== "To be rated" &&
      value.asJson.resubmission !== "Closed",
  )

  const leadershipRatings = store.leadershipRating.all
    .filter(
      (value) =>
        value.asJson.rateeId === me?.uid &&
        value.asJson.adminApproval !== "Approved for Re-rating" &&
        value.asJson.resubmission !== "To be rated" &&
        value.asJson.resubmission !== "Closed",
    )
    .map((value) => value.asJson)

  const templateRatings = store.templateRating.all
    .filter(
      (value) =>
        value.asJson.rateeId === store.auth.meJson?.uid &&
        value.asJson.status === "Completed" &&
        value.asJson.adminApproval !== "Approved for Re-rating" &&
        value.asJson.resubmission !== "To be rated" &&
        value.asJson.resubmission !== "Closed",
    )
    .map((value) => value.asJson)

  const valueScore = useMemo(() => {
    const totals: any = {}
    const counts: any = {}

    valueRatings.forEach((rating) => {
      Object.values(rating.asJson.values).forEach((raterData: any) => {
        Object.entries(raterData.ratings).forEach(([valueName, statements]: any) => {
          if (!totals[valueName]) {
            totals[valueName] = 0
            counts[valueName] = 0
          }
          Object.values(statements).forEach((score: any) => {
            totals[valueName] += score
            counts[valueName] += 1
          })
        })
      })
    })

    const scores: any = {}
    Object.keys(totals).forEach((valueName) => {
      scores[valueName] = totals[valueName] / counts[valueName] || 0
    })

    const scoreValues = Object.values(scores) as number[]
    return scoreValues.length > 0 ? scoreValues.reduce((a, b) => a + b, 0) / scoreValues.length : 0
  }, [valueRatings])

  const leadershipValueScore = useMemo(() => {
    const totals: any = {}
    const counts: any = {}

    leadershipRatings.forEach((rating) => {
      Object.values(rating.criteria).forEach((criteriaData: any) => {
        Object.entries(criteriaData.ratings).forEach(([criteriaName, statements]: any) => {
          if (!totals[criteriaName]) {
            totals[criteriaName] = 0
            counts[criteriaName] = 0
          }
          Object.values(statements).forEach((score: any) => {
            totals[criteriaName] += score
            counts[criteriaName] += 1
          })
        })
      })
    })

    const scores: any = {}
    Object.keys(totals).forEach((criteriaName) => {
      scores[criteriaName] = totals[criteriaName] / counts[criteriaName] || 0
    })

    const scoreValues = Object.values(scores) as number[]
    return scoreValues.length > 0 ? scoreValues.reduce((a, b) => a + b, 0) / scoreValues.length : 0
  }, [leadershipRatings])

  const templateOverallScore = useMemo(() => {
    const totals: any = {}
    const counts: any = {}

    templateRatings.forEach((rating) => {
      Object.values(rating.values).forEach((raterData: any) => {
        Object.entries(raterData.ratings).forEach(([criteriaName, statements]: any) => {
          if (!totals[criteriaName]) {
            totals[criteriaName] = 0
            counts[criteriaName] = 0
          }
          Object.values(statements).forEach((score: any) => {
            totals[criteriaName] += score
            counts[criteriaName] += 1
          })
        })
      })
    })

    const scores: any = {}
    Object.keys(totals).forEach((criteriaName) => {
      scores[criteriaName] = totals[criteriaName] / counts[criteriaName] || 0
    })

    const scoreValues = Object.values(scores) as number[]
    return scoreValues.length > 0 ? scoreValues.reduce((a, b) => a + b, 0) / scoreValues.length : 0
  }, [templateRatings])

  // Calculate overall average based on available ratings
  const ratingAverage = useMemo(() => {
    const scores = [valueScore || 0, leadershipValueScore || 0, templateOverallScore || 0].filter((score) => score > 0)

    return scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0
  }, [valueScore, leadershipValueScore, templateOverallScore])

  const navigate = useNavigate()

  const [openAboutDialog, setOpenAboutDialog] = useState(false)
  const [loading, setLoading] = useState(true)

  const measures = store.measure.allMe
  const $measures = measures.map((measure) => measure.asJson)
  const rating2 = totalQ2MeasureRating($measures)
  const rating4 = totalQ4MeasureRating($measures)

  const midtermPeriod = store.ratingAssignments.all.filter(
    (session) => session.asJson.midtermReview?.status === "In Progress",
  )

  const midtermStartDate = midtermPeriod.map((date) => date.asJson.midtermReview?.startDate)
  const midtermEndDate = midtermPeriod.map((date) => date.asJson.midtermReview?.endDate)
  const finalStartDate = midtermPeriod.map((date) => date.asJson.finalAssessment?.startDate)
  const finalEndDate = midtermPeriod.map((date) => date.asJson.finalAssessment?.endDate)

  const numberOfRequests = store.templateRating.all.filter(
    (request) => (request.asJson.raterId === me?.uid || "") && request.asJson.status === "Not Started",
  ).length

  const getDisplayNameById = (userId: string) => {
    const user = store.user.all.find((user) => user.asJson.uid === userId)?.asJson
    return user ? user.displayName : undefined
  }

  const currentUserId = store.auth.meJson?.uid
  const displayName = currentUserId ? getDisplayNameById(currentUserId) : undefined

  const currentHour = new Date().getHours()
  let greeting
  let GreetingIcon

  if (currentHour < 12) {
    greeting = "Good Morning"
    GreetingIcon = Sunrise
  } else if (currentHour < 18) {
    greeting = "Good Afternoon"
    GreetingIcon = Sun
  } else {
    greeting = "Good Evening"
    GreetingIcon = Moon
  }

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        await api.ratingAssignments.getAll()
        const descriptions = store.ratingAssignments.all.find((rating) => rating.asJson.isActive === true)?.asJson
          .description
        if (descriptions && me) {
          await Promise.all([
            api.valueRating.getAll(me?.uid ?? "", descriptions ?? ""),
            api.leadershipRating.getAll(me?.uid ?? "", descriptions ?? ""),
            api.template.getAll(),
            api.templateRating.getAll(),
            api.measure.getAll(uid),
          ])
        }
      } catch (error) {
        console.error("Error occurred:", error)
      } finally {
        setLoading(false)
      }
    }

    if (me?.uid) {
      load()
    } else {
      setLoading(false)
    }
  }, [
    me,
    uid,
    api.valueRating,
    api.leadershipRating,
    api.templateRating,
    api.ratingAssignments,
    api.template,
    api.measure,
  ])

  const calculateTimeRemaining = (startDate: number, endDate: number) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const now = new Date()

    // Normalize to start of the day
    start.setHours(0, 0, 0, 0)
    end.setHours(0, 0, 0, 0)
    now.setHours(0, 0, 0, 0)

    if (now.getTime() > end.getTime()) {
      return "Time is up"
    }

    const difference = end.getTime() - now.getTime()
    const days = Math.floor(difference / (1000 * 60 * 60 * 24))
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((difference % (1000 * 60)) / 1000)

    if (days > 0) {
      return `${days} day${days > 1 ? "s" : ""}`
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? "s" : ""}`
    } else if (minutes > 0) {
      return `${minutes} min${minutes > 1 ? "s" : ""}`
    } else {
      return `${seconds} sec${seconds > 1 ? "s" : ""}`
    }
  }

  const handleAboutClick = () => {
    setOpenAboutDialog(true)
  }

  const handleCloseAboutDialog = () => {
    setOpenAboutDialog(false)
  }

  const handleTab = (tabName: string) => {
    if (tabName === "Ratings-analysis") {
      navigate("/c/threesixty/home/overview?tab=Ratings-analysis")
    } else if (tabName === "Ratings-reports") {
      navigate("/c/threesixty/home/overview?tab=Reports")
    }
  }

  const onViewTab = (tab: string) => {
    navigate(`/c/threesixty/home/overview?tab=surveys-values`)
  }

  const onViewRequests = (tab: string) => {
    navigate("/c/threesixty/home/overview?tab=requests")
  }

  const onViewMyScoreCard = () => {
    navigate("/c/scorecards/my")
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200"></div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Activity className="h-5 w-5 text-blue-600 animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  const templateScores = templateRatings.flatMap((template) =>
    Object.values(template.values).flatMap((rater) =>
      Object.values(rater.ratings).flatMap((valueName) => Object.values(valueName)),
    ),
  )

  const sampleData = [
    { category: "Values", score: valueScore || 0 },
    { category: "Leadership", score: leadershipValueScore || 0 },
    { category: "Professional Competency", score: templateOverallScore || 0 },
  ]

  return (
    <div className="w-full">
      <div className="space-y-4 sm:space-y-6 animate-fadeIn">
        {/* Welcome Section - Full Width */}
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-blue-100/50">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-purple-600/5 to-pink-600/5"></div>
          <div className="relative text-center space-y-3 sm:space-y-4 lg:space-y-6">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="p-3 sm:p-4 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl sm:rounded-2xl shadow-lg">
                <GreetingIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
              <div className="text-center sm:text-left">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-800 via-blue-800 to-purple-800 bg-clip-text text-transparent">
                  {greeting}
                </h2>
                <p className="text-lg sm:text-xl lg:text-2xl font-semibold text-slate-700 mt-1">
                  {displayName || "User"}
                </p>
              </div>
            </div>

            <div className="space-y-2 sm:space-y-4">
              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed">
                Welcome to your 360° Feedback Dashboard
              </p>
              <p className="text-sm sm:text-base lg:text-lg text-slate-500">
                Track your progress, view ratings, and manage your professional development journey
              </p>
            </div>

            <button
              onClick={handleAboutClick}
              className="inline-flex items-center gap-2 sm:gap-3 bg-white/80 backdrop-blur-sm hover:bg-white text-slate-700 hover:text-blue-600 font-semibold px-4 sm:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl border border-white/50 hover:border-blue-200 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl group text-sm sm:text-base"
            >
              <Info className="h-4 w-4 sm:h-5 sm:w-5" />
              Learn about 360° Feedback
              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          {!store.ratingAssignments.all.some((r) => r.asJson.isActive) && (
            <NoSessionAlert onCreateSession={() => console.log("Open Create Session Modal")} />
          )}
        </div>

        {/* Stats Cards - Full Width Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          {/* My Rating Card */}
          <div
            className="group relative overflow-hidden bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white cursor-pointer hover:scale-105 transition-all duration-500 shadow-xl hover:shadow-2xl hover:shadow-blue-500/25"
            onClick={() => handleTab("Ratings-analysis")}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="p-2 sm:p-3 lg:p-4 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl group-hover:bg-white/30 transition-colors duration-300">
                  <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
                </div>
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 opacity-60 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300" />
              </div>
              <div className="space-y-1 sm:space-y-2">
                <p className="text-blue-100 text-xs sm:text-sm font-medium uppercase tracking-wide">
                  My Overall Rating
                </p>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold">
                  {ratingAverage ? ratingAverage.toFixed(1) : "0.0"}
                </p>
                <div className="flex items-center gap-2 text-blue-100 text-xs sm:text-sm">
                  <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>Performance Score</span>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-2 -right-2 w-16 h-16 sm:w-24 sm:h-24 bg-white/10 rounded-full blur-xl"></div>
          </div>

          {/* Surveys Card */}
          {midtermStartDate[0] && midtermEndDate[0] && (
            <div
              className="group relative overflow-hidden bg-gradient-to-br from-purple-500 via-purple-600 to-violet-700 rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white cursor-pointer hover:scale-105 transition-all duration-500 shadow-xl hover:shadow-2xl hover:shadow-purple-500/25"
              onClick={() => onViewTab("surveys")}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="p-2 sm:p-3 lg:p-4 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl group-hover:bg-white/30 transition-colors duration-300">
                    <Users className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
                  </div>
                  <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 opacity-60 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300" />
                </div>
                <div className="space-y-1 sm:space-y-2">
                  <p className="text-purple-100 text-xs sm:text-sm font-medium uppercase tracking-wide">
                    Active Surveys
                  </p>
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold">
                    {calculateTimeRemaining(midtermStartDate[0], midtermEndDate[0])}
                  </p>
                  <div className="flex items-center gap-2 text-purple-100 text-xs sm:text-sm">
                    <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Time Remaining</span>
                  </div>
                  {finalStartDate[0] && finalEndDate[0] && (
                    <div className="mt-2 sm:mt-3 p-1.5 sm:p-2 bg-white/10 rounded-lg">
                      <p className="text-purple-100 text-xs">
                        Final: {calculateTimeRemaining(finalStartDate[0], finalEndDate[0])}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="absolute -bottom-2 -right-2 w-16 h-16 sm:w-24 sm:h-24 bg-white/10 rounded-full blur-xl"></div>
            </div>
          )}

          {/* Requests Card */}
          {numberOfRequests !== null && (
            <div
              className="group relative overflow-hidden bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white cursor-pointer hover:scale-105 transition-all duration-500 shadow-xl hover:shadow-2xl hover:shadow-emerald-500/25"
              onClick={() => onViewRequests("requests")}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="p-2 sm:p-3 lg:p-4 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl group-hover:bg-white/30 transition-colors duration-300">
                    <FileText className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
                  </div>
                  <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 opacity-60 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300" />
                </div>
                <div className="space-y-1 sm:space-y-2">
                  <p className="text-emerald-100 text-xs sm:text-sm font-medium uppercase tracking-wide">
                    Pending Requests
                  </p>
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-bold">{numberOfRequests}</p>
                  <div className="flex items-center gap-2 text-emerald-100 text-xs sm:text-sm">
                    <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Awaiting Response</span>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-2 -right-2 w-16 h-16 sm:w-24 sm:h-24 bg-white/10 rounded-full blur-xl"></div>
            </div>
          )}

          {/* Score Card */}
          {/* Midterm Score Card */}
          {rating2 !== null && (
            <div
              className="group relative overflow-hidden bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white cursor-pointer hover:scale-105 transition-all duration-500 shadow-xl hover:shadow-2xl hover:shadow-orange-500/25"
              onClick={onViewMyScoreCard}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="p-2 sm:p-3 lg:p-4 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl group-hover:bg-white/30 transition-colors duration-300">
                    <Award className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
                  </div>
                  <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 opacity-60 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300" />
                </div>
                <div className="space-y-2">
                  <p className="text-amber-100 text-xs sm:text-sm font-medium uppercase tracking-wide">Midterm Score</p>
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-bold">{rating2.toFixed(2)}</p>
                  <div className="flex items-center gap-2 text-amber-100 text-xs sm:text-sm">
                    <Award className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Score Card</span>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-2 -right-2 w-16 h-16 sm:w-24 sm:h-24 bg-white/10 rounded-full blur-xl"></div>
            </div>
          )}

          {/* Final Performance Score Card */}
          {rating4 !== null && (
            <div
              className="group relative overflow-hidden bg-gradient-to-br from-pink-500 via-red-500 to-rose-600 rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white cursor-pointer hover:scale-105 transition-all duration-500 shadow-xl hover:shadow-2xl hover:shadow-pink-500/25"
              onClick={onViewMyScoreCard}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="p-2 sm:p-3 lg:p-4 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl group-hover:bg-white/30 transition-colors duration-300">
                    <Award className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
                  </div>
                  <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 opacity-60 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300" />
                </div>
                <div className="space-y-2">
                  <p className="text-amber-100 text-xs sm:text-sm font-medium uppercase tracking-wide">
                    Final Performance Score
                  </p>
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-bold">{rating4.toFixed(2)}</p>
                  <div className="flex items-center gap-2 text-amber-100 text-xs sm:text-sm">
                    <Award className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Score Card</span>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-2 -right-2 w-16 h-16 sm:w-24 sm:h-24 bg-white/10 rounded-full blur-xl"></div>
            </div>
          )}
        </div>

        {/* Charts Section - Full Width */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-all duration-500">
            <RatingsOverview data={sampleData} />
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-all duration-500">
            <UserRatingStatus />
          </div>
        </div>

        {/* About Dialog */}
        <Dialog
          open={openAboutDialog}
          onClose={handleCloseAboutDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: "24px",
              padding: "0",
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
              border: "1px solid #e2e8f0",
              margin: "16px",
              maxHeight: "calc(100vh - 32px)",
            },
          }}
        >
          <DialogActions className="p-3 sm:p-4 border-b border-slate-200">
            <button
              onClick={handleCloseAboutDialog}
              className="p-2 hover:bg-slate-100 rounded-xl transition-colors duration-200 group"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6 text-slate-600 group-hover:text-slate-800" />
            </button>
          </DialogActions>
          <DialogContent className="p-0">
            <Home />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
